/**
 * Simple rich-text editor with token (alias) insertion.
 * Toolbar: font-size, bold, color, align, bullets, link. Tokens shown as pills.
 */
import { useRef, useState, useCallback, useEffect } from "react";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { DialogTrigger, Dialog, ModalOverlay, Modal } from "@/components/application/modals/modal";
import { User01 } from "@untitledui/icons";

// Alias schema: Type (table) -> tokens (column + alias_name)
const ALIAS_SCHEMA: { type: string; table: string; tokens: { column: string; alias_name: string }[] }[] = [
  {
    type: "Contact",
    table: "contacts",
    tokens: [
      { column: "first_name", alias_name: "First Name" },
      { column: "last_name", alias_name: "Last Name" },
      { column: "email", alias_name: "Email" },
    ],
  },
  {
    type: "Company",
    table: "companies",
    tokens: [
      { column: "name", alias_name: "Company Name" },
      { column: "legal_name", alias_name: "Legal Name" },
    ],
  },
  {
    type: "Shipment",
    table: "containers",
    tokens: [
      { column: "container_number", alias_name: "Container Number" },
      { column: "origin_port", alias_name: "Origin Port" },
      { column: "destination_port", alias_name: "Destination Port" },
    ],
  },
];

export interface AliasItem {
  placeholder: string;
  table: string;
  column: string;
  alias_name: string;
}

function placeholderFor(table: string, column: string): string {
  return `{{${table}.${column}}}`;
}

interface RichTextEditorWithTokensProps {
  content: string;
  aliases: AliasItem[];
  onContentChange: (html: string) => void;
  onAliasesChange: (aliases: AliasItem[]) => void;
  placeholder?: string;
}

export function RichTextEditorWithTokens({
  content,
  aliases,
  onContentChange,
  onAliasesChange,
  placeholder = "Scrivi qui...",
}: RichTextEditorWithTokensProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);
  const [insertTokenOpen, setInsertTokenOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<{ column: string; alias_name: string } | null>(null);
  const [_isEditorFocused, setIsEditorFocused] = useState(false);

  const typeOptions = ALIAS_SCHEMA.map((s) => ({ id: s.type, label: s.type }));
  const selectedSchema = ALIAS_SCHEMA.find((s) => s.type === selectedType);
  const tokenOptions = selectedSchema
    ? selectedSchema.tokens.map((t) => ({ id: `${t.column}`, label: t.alias_name, ...t }))
    : [];

  // Sync editor DOM when content prop changes (e.g. template loaded in edit mode)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = content || "";
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [content]);

  const emitContent = useCallback(() => {
    const el = editorRef.current;
    if (el) onContentChange(el.innerHTML || "");
  }, [onContentChange]);

  const handleInput = useCallback(() => {
    emitContent();
  }, [emitContent]);

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  }, []);

  const execCommand = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    emitContent();
  };

  const insertTokenAtCursor = useCallback(
    (item: AliasItem) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();

      const sel = window.getSelection();
      if (!sel) return;

      if (savedSelectionRef.current) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
        savedSelectionRef.current = null;
      } else if (el.childNodes.length > 0) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) return;

      const span = document.createElement("span");
      span.contentEditable = "false";
      span.setAttribute("data-placeholder", item.placeholder);
      span.setAttribute("data-table", item.table);
      span.setAttribute("data-column", item.column);
      span.setAttribute("data-alias-name", item.alias_name);
      span.className =
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-secondary border border-secondary text-sm text-primary align-baseline";
      span.style.display = "inline-flex";
      span.style.verticalAlign = "baseline";
      span.style.whiteSpace = "nowrap";
      span.innerHTML = `<span class="inline-flex size-4 text-quaternary"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>${item.alias_name}`;

      range.deleteContents();
      range.insertNode(span);
      const after = document.createRange();
      after.setStartAfter(span);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      el.focus();

      onAliasesChange([...aliases, item]);
      emitContent();
    },
    [aliases, onAliasesChange, emitContent]
  );

  const handleInsertToken = () => {
    if (!selectedSchema || !selectedToken) return;
    const item: AliasItem = {
      placeholder: placeholderFor(selectedSchema.table, selectedToken.column),
      table: selectedSchema.table,
      column: selectedToken.column,
      alias_name: selectedToken.alias_name,
    };
    setSelectedToken(null);
    setInsertTokenOpen(false);
    requestAnimationFrame(() => {
      insertTokenAtCursor(item);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-secondary bg-secondary/50 p-1">
        <select
          className="rounded border border-secondary bg-primary px-2 py-1 text-sm text-primary"
          onChange={(e) => execCommand("fontSize", e.target.value)}
          defaultValue="3"
        >
          <option value="1">Small</option>
          <option value="2">Normal</option>
          <option value="3">Medium</option>
          <option value="4">Large</option>
        </select>
        <button
          type="button"
          className="rounded px-2 py-1 text-sm font-bold hover:bg-secondary"
          onClick={() => execCommand("bold")}
        >
          B
        </button>
        <input
          type="color"
          className="h-7 w-8 cursor-pointer rounded border border-secondary"
          onInput={(e) => execCommand("foreColor", (e.target as HTMLInputElement).value)}
        />
        <button
          type="button"
          className="rounded px-2 py-1 text-sm hover:bg-secondary"
          onClick={() => execCommand("justifyLeft")}
        >
          ≡
        </button>
        <button
          type="button"
          className="rounded px-2 py-1 text-sm hover:bg-secondary"
          onClick={() => execCommand("justifyCenter")}
        >
          ≡
        </button>
        <button
          type="button"
          className="rounded px-2 py-1 text-sm hover:bg-secondary"
          onClick={() => execCommand("insertUnorderedList")}
        >
          •
        </button>
        <button
          type="button"
          className="rounded px-2 py-1 text-sm hover:bg-secondary"
          onClick={() => {
            const url = prompt("URL:");
            if (url) execCommand("createLink", url);
          }}
        >
          Link
        </button>
        <DialogTrigger isOpen={insertTokenOpen} onOpenChange={setInsertTokenOpen}>
          <Button
            type="button"
            size="sm"
            color="secondary"
            iconLeading={User01}
            onMouseDown={saveSelection}
            onPress={() => setInsertTokenOpen(true)}
          >
            Insert Token
          </Button>
          <ModalOverlay>
            <Modal>
              <Dialog>
                <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                  <h3 className="text-lg font-semibold text-primary mb-4">Insert Token</h3>
                  <div className="space-y-4">
                    <Select
                      label="Type"
                      items={typeOptions}
                      selectedKey={selectedType}
                      onSelectionChange={(key) => {
                        setSelectedType(key as string);
                        setSelectedToken(null);
                      }}
                      size="md"
                    >
                      {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                    <Select
                      label="Token"
                      items={tokenOptions}
                      selectedKey={selectedToken ? selectedToken.column : ""}
                      onSelectionChange={(key) => {
                        const t = selectedSchema?.tokens.find((x) => x.column === key);
                        setSelectedToken(t ?? null);
                      }}
                      size="md"
                      isDisabled={!selectedType}
                      placeholder="Select token"
                    >
                      {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <Button size="md" color="secondary" onPress={() => setInsertTokenOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="md"
                      color="primary"
                      onPress={handleInsertToken}
                      isDisabled={!selectedToken}
                    >
                      Insert
                    </Button>
                  </div>
                </div>
              </Dialog>
            </Modal>
          </ModalOverlay>
        </DialogTrigger>
      </div>
      <div
        ref={editorRef}
        className="min-h-[200px] rounded-b-lg border border-secondary bg-primary px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand [&_ul]:list-disc [&_ul]:pl-6"
        contentEditable
        onInput={handleInput}
        onBlur={() => {
          saveSelection();
          emitContent();
        }}
        onFocus={() => setIsEditorFocused(true)}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        style={{ outline: "none" }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: var(--color-fg-tertiary);
        }
        [contenteditable="true"] [data-placeholder] {
          display: inline-flex !important;
          vertical-align: baseline;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}
